import React, { useEffect, useState, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import Fuse from "fuse.js";
import Box from "@amzn/meridian/box";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Button from "@amzn/meridian/button";
import Divider from "@amzn/meridian/divider";
import Text from "@amzn/meridian/text";
import Icon from "@amzn/meridian/icon";
import Loader from "@amzn/meridian/loader";
import SearchField from "@amzn/meridian/search-field";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import PropTypes from "prop-types";
import {
  useWidgetLocalization,
  getLocalizedText,
} from "../util/WidgetLocalizationUtil";
import { getLocale } from "../../handler/LocaleHandler";
import { getAvailableWidgets } from "../common/thunks/AvailableWidgetsDispatcher";
import { useDispatch, useSelector } from "react-redux";
import {
  addWidgetsToDashboardConfig,
  deleteWidgetsFromDashboardConfig,
  saveDashboardConfiguration,
} from "../common/thunks/DashboardConfigurationDispatcher";
import {
  EDIT_DASHBOARD,
  SELECT_WIDGETS_TO_ADD,
  SEARCH_METRICS,
  SEARCH_WIDGETS_PLACEHOLDER,
  CLOSE_METRICS_PANEL,
  SAVE,
  CONFIG_GROUP_TYPES,
  TOAST_NOTIFICATION_LEVELS,
  TOAST_MESSAGES,
} from "../constants/Constants";
import { useToastNotification } from "../common/context/ToastNotificationContext";

/**
 * Component for selecting widgets
 * Shows ALL available widgets
 * Click to toggle add/remove, Save button to persist changes and close panel
 * Designed to be used inside a Sheet component in the parent
 */

// Helper component to handle localization for individual widgets
const WidgetButton = ({ widget, isInDashboard, onToggle }) => {
  const { title, metricsdescription } = useWidgetLocalization(
    widget.widgetDefinition,
  );

  return (
    <Button
      key={widget.widgetId}
      type={isInDashboard ? "primary" : "secondary"}
      onClick={() => onToggle(widget)}
      size="large"
    >
      <Box>
        <Row width="100%" alignmentHorizontal="center">
          <Text type="h100">{title}</Text>
        </Row>
        {metricsdescription && (
          <Row width="100%" alignmentHorizontal="center">
            <Text type="b100">{metricsdescription}</Text>
          </Row>
        )}
      </Box>
    </Button>
  );
};

WidgetButton.propTypes = {
  widget: PropTypes.object.isRequired,
  isInDashboard: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const WidgetSelectionSheet = ({
  onClose,
  stationType,
  pageSelected,
  viewTypeSelected,
}) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentLocale, setCurrentLocale] = useState(null); // Start with null to prevent premature Fuse initialization
  const availableWidgets = useSelector((state) => state.availableWidgets);
  const unSavedDashboardConfig = useSelector(
    (state) => state.unSavedDashboardConfig,
  );
  const savedDashboardConfig = useSelector((state) => state.dashboardConfig);
  const { sendToastMessage } = useToastNotification();

  // Get current locale for locale-specific search
  useEffect(() => {
    const fetchLocale = async () => {
      try {
        const locale = await getLocale();
        setCurrentLocale(locale || "en");
      } catch (error) {
        console.warn("Failed to get locale, using default 'en':", error);
        setCurrentLocale("en");
      }
    };
    fetchLocale();
  }, []);

  useEffect(() => {
    if (!availableWidgets.widgetDefinitions) {
      dispatch(getAvailableWidgets());
    }
  }, []);

  // Create Fuse instance for locale-specific fuzzy search
  const fuse = useMemo(() => {
    // Don't create Fuse until we have both widgets and a properly loaded locale
    if (!availableWidgets.widgetDefinitions || !currentLocale) return null;

    // Transform widgets to include localized text directly in the object for simpler Fuse.js processing
    const transformedWidgets = availableWidgets.widgetDefinitions.map(
      (widget) => {
        const localizedTitle = getLocalizedText(
          widget.widgetDefinition.title,
          currentLocale,
        );
        const localizedMetrics = getLocalizedText(
          widget.widgetDefinition.metricsdescription,
          currentLocale,
        );
        const localizedDescription = getLocalizedText(
          widget.widgetDefinition.description,
          currentLocale,
        );

        return {
          ...widget,
          // Add localized fields directly to the widget object
          localizedTitle,
          localizedMetrics,
          localizedDescription,
        };
      },
    );

    const fuseInstance = new Fuse(transformedWidgets, {
      keys: [
        // Use simple string keys instead of getFn functions
        {
          name: "localizedTitle",
          weight: 0.7, // Higher weight for title matches
        },
        {
          name: "localizedMetrics",
          weight: 0.2, // Medium weight for metrics description
        },
        {
          name: "localizedDescription",
          weight: 0.1, // Lower weight for general description
        },
      ],
      threshold: 0.6, // Increase threshold for more lenient matching (0 = exact match, 1 = match anything)
      shouldSort: true, // Sort results by score
      minMatchCharLength: 1, // Allow matching single characters
      ignoreLocation: true, // Don't consider location of match in string
    });

    return fuseInstance;
  }, [availableWidgets.widgetDefinitions, currentLocale]);

  // Filter widgets based on search term and exclude widgets already in SAVED dashboard
  const filteredWidgets = useMemo(() => {
    if (!availableWidgets.widgetDefinitions) return [];

    // Get all widgets that aren't in the saved dashboard
    const savedWidgets = savedDashboardConfig.widgets || [];
    const availableWidgetsFiltered = availableWidgets.widgetDefinitions.filter(
      (widget) => {
        const isInSavedDashboard = savedWidgets.some(
          (w) => w.widgetId === widget.widgetId,
        );
        return !isInSavedDashboard;
      },
    );

    // If no search term or no fuse instance, return all available widgets
    if (!searchTerm || !fuse) {
      return availableWidgetsFiltered;
    }

    // Perform fuzzy search and extract the actual widgets from Fuse.js results
    const searchResults = fuse.search(searchTerm);

    // Fuse.js returns results in format: [{item: widget, score: number}]
    // We need to extract the actual widget objects and filter out saved widgets
    const finalResults = searchResults
      .map((result) => result.item)
      .filter((widget) => {
        const isInSavedDashboard = savedWidgets.some(
          (w) => w.widgetId === widget.widgetId,
        );
        return !isInSavedDashboard;
      });

    return finalResults;
  }, [
    availableWidgets.widgetDefinitions,
    savedDashboardConfig.widgets,
    searchTerm,
    fuse,
    currentLocale,
  ]);

  // Handle widget toggle - immediately add or remove from dashboard
  const handleWidgetToggle = (widget) => {
    const dashboardWidgets = unSavedDashboardConfig.widgets || [];
    const isInDashboard = dashboardWidgets.some(
      (w) => w.widgetId === widget.widgetId,
    );
    if (isInDashboard) {
      dispatch(deleteWidgetsFromDashboardConfig(widget.widgetId));
    } else {
      dispatch(addWidgetsToDashboardConfig([widget]));
    }
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    const savedWidgets = savedDashboardConfig.widgets || [];
    const unsavedWidgets = unSavedDashboardConfig.widgets || [];

    // Compare widget arrays to see if there are differences
    if (savedWidgets.length !== unsavedWidgets.length) return true;

    // Check if all saved widgets exist in unsaved (and vice versa)
    return !savedWidgets.every((savedWidget) =>
      unsavedWidgets.some(
        (unsavedWidget) => unsavedWidget.widgetId === savedWidget.widgetId,
      ),
    );
  };

  // Handle save - save dashboard configuration and close panel
  const handleSave = () => {
    sendToastMessage(
      "mySCC.toast.widgetAdded",
      TOAST_MESSAGES.WIDGET_ADDED,
      TOAST_NOTIFICATION_LEVELS.SUCCESS,
    );

    dispatch(
      saveDashboardConfiguration(
        stationType,
        CONFIG_GROUP_TYPES.USER_ALIAS,
        pageSelected,
        viewTypeSelected,
      ),
    );
    onClose();
  };

  return (
    <Column spacingInset="100" height="100%">
      {/* Fixed Header Section */}
      <Box spacingInset="large large medium large">
        <Row alignmentHorizontal="justify" spacing="none">
          <Text type="h300">
            <FormattedMessage
              id="mySCC.widgetSelection.editDashboard"
              defaultMessage={EDIT_DASHBOARD}
            />
          </Text>
          <Button
            type="link"
            onClick={onClose}
            aria-label={CLOSE_METRICS_PANEL}
          >
            <Icon tokens={closeSmallTokens} color="secondary" />
          </Button>
        </Row>
        <Box spacingInset="small 0 medium 0">
          <Text type="b300">
            <FormattedMessage
              id="mySCC.widgetSelection.selectWidgets"
              defaultMessage={SELECT_WIDGETS_TO_ADD}
            />
          </Text>
        </Box>

        <Divider spacingAfter="400" />

        <Box spacingInset="small 0">
          <Text type="b300">
            <FormattedMessage
              id="mySCC.widgetSelection.searchMetrics"
              defaultMessage={SEARCH_METRICS}
            />
          </Text>
        </Box>

        {/* Search Field */}
        <Box>
          <SearchField
            value={searchTerm}
            onChange={setSearchTerm}
            onSubmit={() => {}}
            placeholder={SEARCH_WIDGETS_PLACEHOLDER}
            clearButton={true}
          />
        </Box>
      </Box>

      {/* Scrollable Widget List Section */}
      <Box maxHeight="60%" overflowY="auto" spacingInset="0 large">
        {availableWidgets.loading ? (
          <Row alignmentVertical="center" alignmentHorizontal="center">
            <Loader />
          </Row>
        ) : (
          <Column spacing="400">
            {/* Clickable widget boxes */}
            {filteredWidgets.map((widget) => {
              const dashboardWidgets = unSavedDashboardConfig.widgets || [];
              const isInDashboard = dashboardWidgets.some(
                (w) => w.widgetId === widget.widgetId,
              );
              return (
                <WidgetButton
                  key={widget.widgetId}
                  widget={widget}
                  isInDashboard={isInDashboard}
                  onToggle={handleWidgetToggle}
                />
              );
            })}
          </Column>
        )}
      </Box>

      {/* Fixed Footer Section */}
      <Box spacingInset="large">
        <Divider />
        <Box spacingInset="medium 0 0 0">
          <Row alignmentHorizontal="end" spacing="medium">
            <Button
              type="primary"
              onClick={handleSave}
              disabled={!hasUnsavedChanges()}
            >
              <FormattedMessage
                id="mySCC.widgetSelection.save"
                defaultMessage={SAVE}
              />
            </Button>
          </Row>
        </Box>
      </Box>
    </Column>
  );
};

WidgetSelectionSheet.propTypes = {
  onClose: PropTypes.func.isRequired,
  stationType: PropTypes.string.isRequired,
  pageSelected: PropTypes.string.isRequired,
  viewTypeSelected: PropTypes.string.isRequired,
};

export default WidgetSelectionSheet;



// WEBPACK FOOTER //
// ./src/components/mySCC/components/WidgetSelectionSheet.js